/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Smarts JS
 *| (C) Copyright IBM Corp. 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

import SmartsToVizMapping from './SmartsToVizMapping'

export default class DashboardLearning {
	constructor(options) {
		this.contentIds = []
		this.dashboardToSmartsMapping = null

		this.dashboardApi = options.features['API']
		this.ajaxSvc = this.dashboardApi.getGlassCoreSvc('.Ajax')

		this.userProfile = this.dashboardApi.getGlassCoreSvc('.UserProfile')

		this.dashboardApi.getCanvasWhenReady()
			.then((canvas) => {
				canvas.on('change:content:visualization:type', this._onVisualizationTypeChange, this)
			})

		this._initDashboardToSmartsMapping()

		const lifeCycle = options.features['LifeCycle']
		lifeCycle.registerHandler('save.after', this._onSave.bind(this))
		lifeCycle.registerHandler('saveas.after', this._onSave.bind(this))
	}

	destroy() {
		if (this.dashboardApi && this.dashboardApi.getCanvas()) {
			this.dashboardApi.getCanvas().off('change:content:visualization:type', this._onVisualizationTypeChange, this)
		}
	}

	getAPI() {
		return {}
	}

	_onVisualizationTypeChange({ info }) {
		const { contentId } = info
		//ignore if user switch to 'auto' viz type
		// if( value[0] === 'auto' ){
		if( !this.dashboardApi.getCanvas().getContent(contentId).getFeature('Visualization').isTypeLocked() ){
			this.contentIds = this.contentIds.filter( savedContentId => savedContentId != contentId )
			return
		}

		if (!this.contentIds.includes(contentId)) {
			this.contentIds.push(contentId)
		}
	}

	_canLearnFromProductUsage(){
		return this.userProfile.capabilities.includes('canLearnFromProductUsage') &&
			this.userProfile.userProfileSettings.implicit_learning
	}

	_onSave() {
		if( !this._canLearnFromProductUsage() ){
			return
		}

		if (this.contentIds.length === 0) {
			return
		}

		this._getUserActionPayloads().forEach(payload => {
			if (payload.userActions.length > 0) {
				this._sendLearningRequest(payload)
			}
		})

		this.contentIds = []
	}

	_sendLearningRequest(payload) {
		return this.ajaxSvc.ajax({
			type: 'POST',
			url: 'v1/smarts/user-actions/visualization/online',
			contentType: 'application/json',
			data: JSON.stringify(payload)
		})
			.catch( error => this._logError(error) )
	}

	_getVisualizationFeature(canvas, contentId) {
		const content = canvas.getContent(contentId)
		const state = content.getFeature('state')
		const error = state.getError()
		if (content && !error) {
			return content.getFeature('Visualization')
		}
		return null
	}

	_getUserActionPayloads() {

		let sourceIdToPayloadMap = {}
		const canvas = this.dashboardApi.getCanvas()

		// We need to send one request per datasource.  Build up a map of datasource ID to payload
		this.contentIds.forEach((contentId) => {
			const visFeature = this._getVisualizationFeature(canvas, contentId)
			if (visFeature) {
				const sourceId = visFeature.getDataSource().getAssetId()

				if (!sourceIdToPayloadMap[sourceId]) {
					sourceIdToPayloadMap[sourceId] = {
						sourceModuleId: sourceId,
						userActions: []
					}
				}

				const actionPayload = this._getUserActionPayload(visFeature)
				if (actionPayload) {
					sourceIdToPayloadMap[sourceId].userActions.push(actionPayload)
				}
			}
		})

		// Return an array of the payloads instead of the map used to build them up
		return Object.keys(sourceIdToPayloadMap).map((key) => sourceIdToPayloadMap[key])
	}

	_getUserActionPayload(visFeature) {
		const visType = visFeature.getType()
		const mappingInfo = this.dashboardToSmartsMapping[visType]

		if (!mappingInfo) {
			// Smarts doesn't know about the data player or legacy map.  So only log an error
			// if there's an unknown vis type that's not one of those
			if (!this.supportedVisTypes.has(visType)) {
				this._logError(`SmartsDashboardLearning - missing mapping info for chart type ${visType}`)
			}
			return null
		}

		if(!visFeature.getSlots().isMappingComplete()){
			return null
		}

		return {
			actionType: 'VISUALIZATION',
			learningDirective: 'USE',
			actionPayload: {
				chartType: visType,
				columns: this._getColumnsPayload(visFeature, mappingInfo)
			}
		}
	}

	_getColumnsPayload(visFeature, mappingInfo) {
		let columnsPayload = []
		visFeature.getSlots().getMappedSlotList().forEach((mappedSlot) => {
			const mappedSlotId = mappingInfo[mappedSlot.getDefinition().getId()]
			if (mappedSlotId) {
				let slotInfo = {
					id: [],
					slot: mappedSlotId
				}

				// Get the column IDs that are mapped to the slot
				mappedSlot.getDataItemList().forEach(dataItem => slotInfo.id.push(dataItem.getColumnId()))

				columnsPayload.push(slotInfo)
			} else {
				this._logError( `SmartsDashboardLearning - missing slot mapping info for slot ${mappedSlot.getDefinition().getId()}`)
			}
		})

		return columnsPayload
	}

	_logError(error) {
		this.dashboardApi.getGlassCoreSvc('.Logger').error(error)
	}

	/**
	 * Take the SmartsToVizMapping and swap the slot names so we can remap from dashboard
	 * slot names to smart slot names
	 */
	_initDashboardToSmartsMapping() {
		if (this.dashboardToSmartsMapping) {
			return
		}

		this.dashboardToSmartsMapping = {}

		const visTypes = Object.keys( SmartsToVizMapping )
		this.supportedVisTypes = new Set(visTypes)

		visTypes.forEach((key) => {
			let mappingInfo = {}
			let visInfo = SmartsToVizMapping[key]
			Object.keys(visInfo).forEach((visProp) => {
				mappingInfo[visInfo[visProp]] = visProp
			})
			this.dashboardToSmartsMapping[key] = mappingInfo
		})
	}
}
